import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
    FaFacebookF, FaInstagram, FaWhatsapp, FaPhoneAlt, FaEnvelope, FaMapMarkerAlt
} from 'react-icons/fa';
import { FaTiktok } from 'react-icons/fa6';

import contactUsBg from '../assets/Mahi/115.jpeg';

const fadeUp = {
    hidden: { opacity: 0, y: 35 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } }
};

const scaleIn = {
    hidden: { opacity: 0, scale: 0.94 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.7, ease: 'easeOut' } }
};

const ContactUs = () => {
    const { t } = useTranslation('contact');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        contactNumber: '',
        message: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSuccessMessage('');
        setErrorMessage('');

        const phoneNumber = '94766111678';
        const text = `${t('whatsappSubject') || 'New Contact from Website'}\n\n` +
            `${t('whatsappName') || 'Name'}: ${formData.name}\n` +
            `${t('whatsappEmail') || 'Email'}: ${formData.email}\n` +
            `${t('whatsappContactNumber') || 'Phone'}: ${formData.contactNumber || '-'}\n` +
            `${t('whatsappMessage') || 'Message'}:\n${formData.message}`;

        const formattedMessage = encodeURIComponent(text);
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${formattedMessage}`;

        try {
            window.open(whatsappUrl, '_blank');
            setTimeout(() => {
                setIsSubmitting(false);
                setSuccessMessage(t('successMessage') || 'Message sent! We will reply soon.');
                setFormData({ name: '', email: '', contactNumber: '', message: '' });
            }, 1200);
        } catch (err) {
            setIsSubmitting(false);
            setErrorMessage(t('errorMessage') || 'Something went wrong. Please try again.');
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-stone-50 via-[#f9f7f0] to-[#e8e4d6]">

            {/* ─── HERO ──────────────────────────────────────────────── */}
            <header className="relative h-[65vh] min-h-[480px] flex items-center justify-center overflow-hidden">
                <img
                    src={contactUsBg}
                    alt={t('heroAlt') || 'Mahi Beach Ayurveda Resort'}
                    className="absolute inset-0 h-full w-full object-cover brightness-[0.65] scale-[1.02]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/45 to-black/60" />

                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={fadeUp}
                    className="relative z-10 text-center px-5 sm:px-8 max-w-5xl"
                >
                    <h1 className="
            text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl
            font-serif font-light tracking-tight text-white
            drop-shadow-[0_4px_16px_rgba(0,0,0,0.8)]
          ">
                        {t('heroTitle')}
                    </h1>

                    <p className="
            mt-5 sm:mt-7 md:mt-9
            text-xl sm:text-2xl md:text-3xl lg:text-4xl
            font-medium text-amber-300/95 tracking-wide
            drop-shadow-md
          ">
                        {t('heroSubtitle')}
                    </p>
                </motion.div>
            </header>

            {/* ─── MAIN CONTENT ──────────────────────────────────────── */}
            <main className="relative z-10 pb-16 md:pb-24 pt-12 md:pt-16">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12">

                    <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">

                        {/* ─── LEFT ── Contact Info + Social ─────────────────────── */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-100px" }}
                            variants={fadeUp}
                            className="space-y-12 lg:space-y-16"
                        >
                            <div className="text-center lg:text-left">
                                <div className="inline-flex items-center gap-4 mb-5">
                                    <div className="h-px w-12 md:w-16 bg-amber-700/40 flex-1" />
                                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-light text-[#1A2F2A] tracking-tight">
                                        {t('sectionTitle')}
                                    </h2>
                                    <div className="h-px w-12 md:w-16 bg-amber-700/40 flex-1" />
                                </div>

                                <h3 className="text-2xl sm:text-3xl md:text-4xl font-light text-[#2D3F3A] mt-6">
                                    {t('sectionSubtitle')}
                                </h3>

                                <p className="mt-5 text-lg sm:text-xl text-teal-800/85 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                                    {t('sectionDescription')}
                                </p>
                            </div>

                            {/* Contact Cards */}
                            <div className="grid sm:grid-cols-2 gap-6 lg:gap-8">

                                <motion.div
                                    variants={scaleIn}
                                    className="bg-white/70 backdrop-blur-sm rounded-2xl p-7 shadow-lg border border-teal-100/60 hover:border-amber-200/50 transition-all duration-400 hover:shadow-xl"
                                >
                                    <div className="flex items-center gap-4 mb-5">
                                        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-xl">
                                            <FaPhoneAlt />
                                        </div>
                                        <h4 className="text-xl md:text-2xl font-medium text-[#1A2F2A]">
                                            {t('callUs')}
                                        </h4>
                                    </div>
                                    <div className="space-y-2 text-teal-800 text-lg">
                                        <p>+94 773 036 444</p>
                                        <p>+94 76 611 1678</p>
                                        <p>+94 777 194 904</p>
                                    </div>
                                </motion.div>

                                <motion.div
                                    variants={scaleIn}
                                    className="bg-white/70 backdrop-blur-sm rounded-2xl p-7 shadow-lg border border-teal-100/60 hover:border-amber-200/50 transition-all duration-400 hover:shadow-xl"
                                >
                                    <div className="flex items-center gap-4 mb-5">
                                        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-xl">
                                            <FaEnvelope />
                                        </div>
                                        <h4 className="text-xl md:text-2xl font-medium text-[#1A2F2A]">
                                            {t('emailTitle')}
                                        </h4>
                                    </div>
                                    <p className="text-teal-800 text-lg break-all sm:break-normal">
                                        mahibeach23@gmail.com
                                    </p>
                                </motion.div>

                                <motion.div
                                    variants={scaleIn}
                                    className="bg-white/70 backdrop-blur-sm rounded-2xl p-7 shadow-lg border border-teal-100/60 hover:border-amber-200/50 transition-all duration-400 hover:shadow-xl sm:col-span-2 lg:col-span-1"
                                >
                                    <div className="flex items-center gap-4 mb-5">
                                        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-xl">
                                            <FaMapMarkerAlt />
                                        </div>
                                        <h4 className="text-xl md:text-2xl font-medium text-[#1A2F2A]">
                                            {t('ourLocation')}
                                        </h4>
                                    </div>
                                    <p className="text-teal-800 leading-relaxed text-lg">
                                        {t('locationAddress')}
                                    </p>
                                </motion.div>

                            </div>

                            {/* Social Icons */}
                            <div className="text-center lg:text-left">
                                <h4 className="text-xl md:text-2xl font-medium text-[#1A2F2A] mb-6">
                                    {t('socialNetwork')}
                                </h4>
                                <div className="flex justify-center lg:justify-start gap-5">
                                    {[
                                        { href: "https://www.facebook.com/share/1BfGRTr59L/?mibextid=wwXIfr", icon: FaFacebookF, label: t('ariaFacebook') },
                                        { href: "https://www.instagram.com/mahibeach_ayurveda?igsh=bjExYzI0NWY4azNu", icon: FaInstagram, label: t('ariaInstagram') },
                                        { href: "https://wa.me/94766111678", icon: FaWhatsapp, label: t('ariaWhatsApp') },
                                        { href: "https://www.tiktok.com/@.mahi.beach.ayurv?_r=1&_t=ZS-94BSC88Jb2S", icon: FaTiktok, label: t('ariaTikTok') },
                                    ].map((link, i) => (
                                        <motion.a
                                            key={i}
                                            href={link.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={link.label}
                                            className="w-12 h-12 rounded-full bg-white/80 flex items-center justify-center text-amber-700 hover:bg-amber-600 hover:text-white shadow-md hover:shadow-lg transition-all duration-300 text-xl"
                                            whileHover={{ scale: 1.12, y: -4 }}
                                        >
                                            <link.icon />
                                        </motion.a>
                                    ))}
                                </div>
                            </div>
                        </motion.div>

                        {/* ─── RIGHT ── Contact Form ─────────────────────────────── */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-100px" }}
                            variants={scaleIn}
                            className="bg-white/75 backdrop-blur-md rounded-2xl md:rounded-3xl shadow-2xl border border-teal-100/70 p-8 lg:p-10 xl:p-12"
                        >
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-light text-[#1A2F2A] mb-4">
                                {t('getInTouch')}
                            </h2>
                            <p className="text-lg md:text-xl text-teal-800/85 mb-10">
                                {t('getInTouchDescription')}
                            </p>

                            <form onSubmit={handleSubmit} className="space-y-7">
                                {[
                                    { label: t('nameLabel'), name: 'name', type: 'text', required: true },
                                    { label: t('emailLabel'), name: 'email', type: 'email', required: true },
                                    { label: t('contactNumberLabel'), name: 'contactNumber', type: 'tel' },
                                    { label: t('messageLabel'), name: 'message', type: 'textarea', required: true },
                                ].map((field, i) => (
                                    <div key={i}>
                                        <label className="block text-lg font-medium text-[#2D3F3A] mb-2">
                                            {field.label}
                                        </label>
                                        {field.type === 'textarea' ? (
                                            <textarea
                                                name={field.name}
                                                value={formData[field.name]}
                                                onChange={handleChange}
                                                rows={5}
                                                required={field.required}
                                                className="
                          w-full px-5 py-4 rounded-xl border border-teal-200
                          bg-white/60 focus:bg-white focus:border-amber-400
                          text-teal-900 text-lg outline-none transition-all
                          placeholder:text-teal-400/70 resize-y min-h-[120px]
                        "
                                            />
                                        ) : (
                                            <input
                                                type={field.type}
                                                name={field.name}
                                                value={formData[field.name]}
                                                onChange={handleChange}
                                                required={field.required}
                                                className="
                          w-full px-5 py-4 rounded-xl border border-teal-200
                          bg-white/60 focus:bg-white focus:border-amber-400
                          text-teal-900 text-lg outline-none transition-all
                          placeholder:text-teal-400/70
                        "
                                            />
                                        )}
                                    </div>
                                ))}

                                <div className="pt-4">
                                    {successMessage && (
                                        <p className="text-green-600 font-medium text-lg mb-4">{successMessage}</p>
                                    )}
                                    {errorMessage && (
                                        <p className="text-red-600 font-medium text-lg mb-4">{errorMessage}</p>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className={`
                      w-full md:w-auto px-10 py-5 rounded-full
                      font-semibold text-lg md:text-xl
                      shadow-lg hover:shadow-xl transform hover:-translate-y-1
                      transition-all duration-300
                      ${isSubmitting
                                                ? 'bg-gray-400 cursor-not-allowed'
                                                : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white'
                                            }
                    `}
                                    >
                                        {isSubmitting ? t('sending') || 'Sending...' : t('sendMessage') || 'Send Message'}
                                    </button>
                                </div>
                            </form>
                        </motion.div>

                    </div>
                </div>
            </main>

            {/* ─── MAP ───────────────────────────────────────────────── */}
            <section className="py-16 md:py-24 bg-gradient-to-b from-[#e8e4d6] to-stone-100">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12">
                    <motion.h2
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeUp}
                        className="text-3xl sm:text-4xl md:text-5xl font-serif font-light text-[#1A2F2A] text-center mb-10 md:mb-16"
                    >
                        {t('findUsOnMap')}
                    </motion.h2>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={scaleIn}
                        className="rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl border border-teal-100/60"
                    >
                        <iframe
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5120.210790847369!2d79.98001160000001!3d6.517360200000001!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae231004803f62d%3A0x1784d9465119747e!2sMahi%20Beach%20Ayurveda%20Resort!5e1!3m2!1sen!2slk!4v1771676661359!5m2!1sen!2slk"
                            width="100%"
                            height="500"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title={t('findUsOnMap')}
                        />
                    </motion.div>
                </div>
            </section>

        </div>
    );
};

export default ContactUs;